import React, { useCallback, useContext } from 'react'
import { INTL_DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import moment from 'moment'
import TextLink from 'LuxKit/TextLink'
import Caption from 'LuxKit/Typography/Caption'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import { initialiseBnblOfferModal } from 'actions/BnblModalActions'
import { OfferPackageOptionView, getBnblPackageViews } from 'selectors/offerPage/offerPageSelectors'
import { useAppDispatch } from 'hooks/reduxHooks'
import useToggle from 'hooks/useToggle'
import BnblModal from './BnblModal/BnblModal'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import LineGiftIcon from 'LuxKit/Icons/line/LineGiftIcon'
import getDatasetFromEvent from 'lib/analytics/getDatasetFromEvent'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import Group from 'components/utils/Group'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { getBnblPackageViewByNumberOfRooms } from './BnblModal/utils'
import { EmptyObject } from 'lib/object/objectUtils'

interface Props {
  offer: App.HotelOffer;
  baseBnblPackageViews: Array<OfferPackageOptionView>;
  packageView?: OfferPackageOptionView;
  rooms: Array<App.Occupants>;
  direction?: 'vertical' | 'horizontal';
  onOpenModal?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

function OfferPageBNBLcta(props: Props) {
  const dispatch = useAppDispatch()
  const intlDateFormatter = useIntlDateFormatter()
  const [bnblModalOpen, _, openBnblModal, closeBnblModal] = useToggle()
  const { offer, baseBnblPackageViews, packageView = EmptyObject, rooms, direction = 'vertical', onOpenModal } = props

  const isSevenDaysBeforeTravelToDate = moment().add(1, 'w').isBefore(offer.travelToDate)
  const isMemberOnlyOffer = offer.luxPlus.access === 'memberOnly'

  const { trackEvent } = useContext(OfferPageStateTrackingContext)

  const openAndInitaliseBnblModal = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    trackEvent('BUY_NOW_CHOOSE_DATES_LATER_PRESSED')
    e.preventDefault()
    openBnblModal()

    if (onOpenModal) {
      onOpenModal(e)
      return
    }

    const isGift = getDatasetFromEvent(e, 'bnbltype') === 'gift'
    const numberOfRooms = rooms?.length ?? 1
    const bnblPackageView = baseBnblPackageViews.find(baseBnblPackageView => baseBnblPackageView.package.uniqueKey === packageView?.package.uniqueKey)
    if (!bnblPackageView) return

    const packages = [{ packageView: getBnblPackageViewByNumberOfRooms(bnblPackageView, numberOfRooms), numberOfRooms }]
    dispatch(initialiseBnblOfferModal(packages, offer.id, isGift))
  }, [baseBnblPackageViews, dispatch, offer.id, openBnblModal, packageView, rooms?.length, trackEvent, onOpenModal])

  return (<>
    <Group wrap={direction === 'horizontal' ? 'wrap' : 'nowrap'} direction={direction} gap={direction === 'horizontal' ? '4 16' : 4}>
      <TextLink
        size="caption"
        onClick={openAndInitaliseBnblModal}
        data-testid="buyNowChooseDatesLater"
        startIcon={<LineCalendarIcon />}
      >
        Buy now, choose dates later
      </TextLink>

      {isSevenDaysBeforeTravelToDate && !isMemberOnlyOffer && <TextLink
        data-bookingtype="gift"
        size="caption"
        onClick={openAndInitaliseBnblModal}
        data-testid="sendAsGift"
        data-bnbltype="gift"
        startIcon={<LineGiftIcon />}
      >
        Give as gift
      </TextLink>}
      {!isSevenDaysBeforeTravelToDate && offer.travelToDate && <Caption variant="medium">
        Travel until {intlDateFormatter(offer.travelToDate, INTL_DMY_CASUAL_FORMAT)}
      </Caption>}
    </Group>
    {bnblModalOpen && <BnblModal closeBnblModal={closeBnblModal} isOpen={bnblModalOpen} offer={offer} />}
  </>)
}

export default withOfferPageState((state) => ({
  rooms: state.rooms,
  offer: state.offer,
  baseBnblPackageViews: getBnblPackageViews(state),
}), OfferPageBNBLcta)
